/** The form for a new sourcing job (moved out of SourcingPanel, Oct 19 2026). */
import { useEffect, useRef, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { Tooltip, TooltipContent, TooltipTrigger } from '@finn-auto/design-system/atoms/tooltip';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import * as api from '../../services/taOutreachApi';
import type { SourcingJob } from '../../types/taOutreach';
import { canSearch, extensionVersion } from './outreachExtension';
import { SearchBox } from './SearchBox';
import { SearchRules } from './SearchRules';
import { useRoleSearch } from './useRoleSearch';

/** A Recruiter search the recruiter made themselves: pasted, it is read as it is (the override). */
const OWN_SEARCH = /(\/discover\/recruiterSearch|\/talent\/search)\?.*searchContextId=/;

/** How long the "keep the LinkedIn window open" note is shown before the window opens. */
export const NOTICE_SECONDS = 8;
const HIDE_NOTICE_KEY = 'ta-outreach-sourcing-notice-hidden-v1';
const noticeHidden = () => {
  try {
    return window.localStorage.getItem(HIDE_NOTICE_KEY) === '1';
  } catch {
    return false;
  }
};
const hideNotice = (hide: boolean) => {
  try {
    if (hide) window.localStorage.setItem(HIDE_NOTICE_KEY, '1');
    else window.localStorage.removeItem(HIDE_NOTICE_KEY);
  } catch {
    /* blocked storage: the note simply shows again next time */
  }
};

/**
 * One click creates the job AND starts it (Sofia, Oct 19 2026: "Create", then "Start" was one step
 * too many). `onCreated` starts the run; without the extension the job is not created at all.
 */
export function JobForm({
  postingId,
  roleTitle,
  onCreated,
  canRun,
  running,
  savedUrl,
}: {
  postingId: string;
  roleTitle: string;
  onCreated: (j: SourcingJob) => void;
  canRun: boolean;
  running: boolean;
  /** The Recruiter link of this role's newest job: the form starts with it (Sofia, Oct 19 2026). */
  savedUrl: string | null;
}) {
  // 50 by default (Sofia, Oct 19 2026): the shortlist is where you pick, so it may be long.
  const [target, setTarget] = useState('50');
  const [maxScreened, setMaxScreened] = useState('250');
  // The Recruiter link stays: what was typed for this role (this browser), else the link of the
  // role's newest job (stored with the job, so any browser). Editable as before.
  const draftKey = `ta-outreach-recruiter-link-v1:${postingId}`;
  const [sourceUrl, setSourceUrlState] = useState(() => {
    try {
      return window.localStorage.getItem(draftKey) || '';
    } catch {
      return '';
    }
  });
  const setSourceUrl = (v: string) => {
    setSourceUrlState(v);
    try {
      if (v.trim()) window.localStorage.setItem(draftKey, v.trim());
      else window.localStorage.removeItem(draftKey);
    } catch {
      /* blocked storage: the newest job's link still fills the field */
    }
  };
  // The jobs load after the form: take their link only while the field is still empty.
  const filledFromJob = useRef(false);
  useEffect(() => {
    if (filledFromJob.current || !savedUrl) return;
    filledFromJob.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- prefilling from data that arrived later
    setSourceUrlState((cur) => cur || savedUrl);
  }, [savedUrl]);
  const [busy, setBusy] = useState(false);
  /**
   * Stage 2 (Theresa, Oct 20 2026): by default the run puts the search below into the project's
   * Recruiter search itself — the recruiter pastes only the project's link. A pasted search address
   * of their own is read as it is instead.
   */
  const roleSearch = useRoleSearch(postingId);
  // The kick-off sheet named the role's Recruiter project: an empty link field takes it (derived, Oct 22 2026).
  const projectFromSheet = roleSearch.projectId ? `https://www.linkedin.com/talent/hire/${roleSearch.projectId}/manage/all` : null;
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- prefilling from data that arrived later
    if (projectFromSheet) setSourceUrlState((cur) => cur || projectFromSheet);
  }, [projectFromSheet]);
  const ownSearch = OWN_SEARCH.test(sourceUrl);
  const keywords = !ownSearch ? (roleSearch.draft ?? roleSearch.search?.boolean ?? '').trim() : '';
  const [searchReady, setSearchReady] = useState(true);
  useEffect(() => {
    let live = true;
    void extensionVersion().then((v) => live && setSearchReady(canSearch(v)));
    return () => {
      live = false;
    };
  }, []);
  /**
   * After the click, the note says what is about to happen and the LinkedIn window opens only
   * NOTICE_SECONDS later, so it can be read first (Sofia, Oct 19 2026). "Don't show this again" is
   * remembered in this browser.
   */
  const [notice, setNotice] = useState<{ job: SourcingJob; left: number; started: boolean } | null>(null);
  const [dontShow, setDontShow] = useState(false);
  // The parent passes a new function each render; the countdown must not restart because of it.
  const onCreatedRef = useRef(onCreated);
  useEffect(() => {
    onCreatedRef.current = onCreated;
  }, [onCreated]);
  // Count down once a second; at 0 the run starts — in an effect, never inside a state updater
  // (React may call an updater twice, which would start two runs).
  useEffect(() => {
    if (!notice || notice.started) return;
    if (notice.left <= 0) {
      const job = notice.job;
      // The note STAYS after the start (Sofia, Oct 19 2026: it vanished before it could be read or
      // "Don't show this again" ticked); the recruiter closes it.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- ending the countdown is the effect
      setNotice({ ...notice, started: true });
      onCreatedRef.current(job);
      return;
    }
    const t = setTimeout(() => setNotice((n) => (n ? { ...n, left: n.left - 1 } : n)), 1000);
    return () => clearTimeout(t);
  }, [notice]);
  function startAfterNotice(job: SourcingJob) {
    if (noticeHidden()) onCreated(job);
    else setNotice({ job, left: NOTICE_SECONDS, started: false });
  }
  async function create() {
    setBusy(true);
    try {
      // The string on screen, saved first: an edit made just before the click is what runs (PR #665).
      const runKeywords = keywords ? await roleSearch.commit() : null;
      if (keywords && !runKeywords) return;
      const { job } = await api.createSourcingJob({
        postingId,
        roleTitle,
        target: Number(target) || 50,
        // Focus / Leave out are gone from the form (Sofia, Oct 19 2026): the Recruiter search is the filter.
        focus: '',
        exclude: '',
        maxProfiles: 25,
        // A project link makes the search run IN the project (its location filter, its pipeline); without
        // one it runs across all of Recruiter (Oct 22 2026). Own search / Pipeline: read as they are.
        ...(sourceUrl.trim() ? { sourceUrl: sourceUrl.trim() } : {}),
        maxScreened: Number(maxScreened) || 250,
        ...(runKeywords ? { searchKeywords: runKeywords } : {}),
      });
      startAfterNotice(job);
      void logAnalyticsEvent('ta_outreach_sourcing_job_created', { target: job.target, max_profiles: job.maxScreened ?? job.maxProfiles });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not create the job.' });
    } finally {
      setBusy(false);
    }
  }
  const blocked = !sourceUrl.trim() && !keywords
    ? 'Build the search above, or paste a Recruiter link (your own search, or a project for its Pipeline).'
    : !canRun || (keywords && !searchReady)
      ? 'Install or update the FINN Outreach Sender first — follow the yellow box at the top of this step.'
      : !ownSearch && roleSearch.building
        ? 'The search is still being built from the brief.'
        : running
        ? 'A sourcing run is going on — wait for it or stop it below.'
        : null;
  const startButton = (
    <Button variant="primary" size="sm" className="rounded-md" disabled={busy || Boolean(notice && !notice.started) || Boolean(blocked)} onClick={() => void create()}>
      {busy ? 'Starting…' : 'Start sourcing job'}
    </Button>
  );
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <SearchBox state={roleSearch} disabled={running || busy || ownSearch} />
        <div className="mt-2">
          <SearchRules postingId={postingId} roleSearch={roleSearch} disabled={running || busy || ownSearch} />
        </div>
      </div>
      <div className="sm:col-span-2">
        <Input
          label="Recruiter project link (filled from the kick-off sheet when it names one)"
          value={sourceUrl}
          maxLength={1000}
          onChange={(e) => setSourceUrl(e.target.value)}
        />
        {(sourceUrl.trim() || keywords) && (
          <p className={`m-0 mt-1 text-xs ${ownSearch || keywords ? 'text-[#1D9247]' : 'text-[#B96716]'}`}>
            {ownSearch
              ? 'Reads your own Recruiter search at this address — the search above is not used.'
              : keywords && sourceUrl.trim()
                ? 'Runs the search above in this project — with the project’s location and filters — then reads it page by page. New people.'
                : keywords
                ? 'No project: the search runs across all of LinkedIn Recruiter, without the project’s location filter. Paste the role’s project link to narrow it.'
                : 'Reads the project’s Pipeline — only people already saved in the project.'}
          </p>
        )}
      </div>
      <Input label="Candidates for the shortlist" type="number" min={10} max={100} step={10} value={target} onChange={(e) => setTarget(e.target.value)} />
      <Input label="Candidates to check at most" type="number" min={25} max={1000} step={25} value={maxScreened} onChange={(e) => setMaxScreened(e.target.value)} />
      {notice && (
        <div role="note" className="m-0 space-y-2 rounded-md border border-solid border-[#DBEBFC] bg-[#F3F8FE] px-4 py-3 text-[13px] text-[#1A1A1A] sm:col-span-2">
          <p className="m-0">
            <b>{notice.started ? 'A LinkedIn window is open next to this one' : `A LinkedIn window opens next to this one in ${notice.left} s`}</b> and
            reads the list page by page. Please do not close it while sourcing runs — closing it stops the run. It closes by itself when it is
            done.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-[#707070]">
              <input
                type="checkbox"
                checked={dontShow}
                onChange={(e) => {
                  setDontShow(e.target.checked);
                  hideNotice(e.target.checked);
                }}
              />
              Don’t show this again
            </label>
            <button
              type="button"
              className="text-xs text-[#0072EA] underline"
              onClick={() => {
                if (!notice.started) onCreatedRef.current(notice.job);
                setNotice(null);
              }}
            >
              {notice.started ? 'Close' : 'Close and start now'}
            </button>
          </div>
        </div>
      )}
      <div className="sm:col-span-2">
        {/* Why the button is greyed out shows on hover only (Sofia, Oct 19 2026), not as text beside it. */}
        {blocked ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-block cursor-not-allowed">
                {startButton}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs text-white">{blocked}</TooltipContent>
          </Tooltip>
        ) : (
          startButton
        )}
      </div>
    </div>
  );
}
